import React, { useEffect, useState } from 'react'
import { usePrivacyComplianceAccess } from '../../hooks/privacy-compliance/usePrivacyComplianceAccess.hooks'
import { useOneTrustConfig } from '../../hooks/privacy-compliance/useOneTrustConfig.hooks'
import { initAnalytics, initDataDogRum, updateDatadogConsent } from '../../utils/events/analytics'
import {
  hasConsentForCategory,
  OneTrustCategory,
  onOneTrustReady,
} from '../../utils/privacy-compliance/oneTrustConsent.utils'
import {
  detectUserRegion,
  type GeolocationResult,
} from '../../utils/privacy-compliance/geolocation.utils'
import { OneTrustCookieBanner } from './OneTrustCookieBanner'

/**
 * Privacy Compliance Provider for authenticated pages
 * Manages IP-based region detection and OneTrust consent banner
 */

const handleConsentChange = (): void => {
  const hasPerformanceConsent = hasConsentForCategory(OneTrustCategory.PERFORMANCE)

  console.log('[Privacy Compliance] Consent changed:', {
    hasPerformanceConsent,
    timestamp: new Date().toISOString(),
    activeGroups: window.OnetrustActiveGroups,
  })

  if (hasPerformanceConsent) {
    void initAnalytics(true)
    initDataDogRum()
  }

  updateDatadogConsent(hasPerformanceConsent)
}

export const PrivacyComplianceProvider: React.FunctionComponent = () => {
  const hasPrivacyComplianceAccess = usePrivacyComplianceAccess()
  const oneTrustConfig = useOneTrustConfig()
  const [shouldLoadOneTrust, setShouldLoadOneTrust] = useState<boolean | null>(null)
  const [isCheckingRegion, setIsCheckingRegion] = useState(true)

  useEffect(() => {
    const checkRegionAndInitialize = async () => {
      const region: GeolocationResult = await detectUserRegion()

      console.log('[Privacy Compliance] Region detected:', {
        country: region.countryCode || 'Unknown',
        isEU: region.isEU,
        requiresConsent: region.requiresConsent,
        method: region.detectionMethod,
      })

      setShouldLoadOneTrust(region.requiresConsent)
      setIsCheckingRegion(false)

      if (region.requiresConsent) {
        console.log('[Privacy Compliance] EU region - loading OneTrust')

        onOneTrustReady(handleConsentChange)

        const checkInitialConsent = () => {
          if (window.OnetrustActiveGroups) {
            handleConsentChange()
          }
        }

        checkInitialConsent()
        setTimeout(checkInitialConsent, 100)
      } else {
        console.log('[Privacy Compliance] Non-EU region - auto-enabling analytics')
        void initAnalytics(true)
        initDataDogRum()
        updateDatadogConsent(true)
      }
    }

    void checkRegionAndInitialize()
  }, [])

  // Don't render anything if feature is not enabled or config is missing
  if (!hasPrivacyComplianceAccess || !oneTrustConfig) {
    return null
  }

  // Still checking region
  if (isCheckingRegion) {
    return null
  }

  // Only render OneTrust banner for EU users
  if (shouldLoadOneTrust) {
    return <OneTrustCookieBanner config={oneTrustConfig} enabled={hasPrivacyComplianceAccess} />
  }

  // Non-EU users don't need OneTrust
  return null
}

export default PrivacyComplianceProvider
